import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LoginIcon from '@mui/icons-material/Login';
import logo from '../assets/img/logo_main.png';
import montblanc from '../assets/img/montblanc.jpg';
import './LoginPage.css';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('login-theme');
    if (saved) return saved === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const particlesRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const container = particlesRef.current;
    if (!container) return;
    container.innerHTML = '';
    for (let i = 0; i < 50; i++) {
      const p = document.createElement('div');
      p.className = 'login-particle';
      p.style.left = Math.random() * 100 + '%';
      p.style.top = '-10px';
      p.style.animationDelay = Math.random() * 10 + 's';
      p.style.animationDuration = (8 + Math.random() * 12) + 's';
      const size = 2 + Math.random() * 5;
      p.style.width = size + 'px';
      p.style.height = size + 'px';
      p.style.opacity = String(0.3 + Math.random() * 0.7);
      container.appendChild(p);
    }
  }, []);

  const toggleTheme = () => {
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem('login-theme', next ? 'dark' : 'light');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authService.login(email, password);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha no login. Verifique suas credenciais.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`login-page${darkMode ? ' dark' : ''}`}>
      <div className="login-bg" style={{ backgroundImage: `url(${montblanc})` }} />
      <div className="login-overlay" />
      <div className="login-particles" ref={particlesRef} />

      <button className="login-theme-toggle" onClick={toggleTheme} title="Alternar tema">
        {darkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
      </button>

      <div className="login-container">
        <div className="login-card">
          <div className="login-logo">
            <img src={logo} alt="Mont Capital" />
            <div className="login-logo-title">ProFunds</div>
            <div className="login-logo-sub">by Mont Asset</div>
          </div>

          <form onSubmit={handleLogin} noValidate autoComplete="off">
            {error && (
              <div className="login-error">
                <span>⚠ {error}</span>
              </div>
            )}

            <div className="login-field">
              <label htmlFor="login-email">
                <PersonOutlineIcon fontSize="small" />
                <span>Usuário</span>
              </label>
              <input
                id="login-email"
                type="text"
                placeholder="Digite seu usuário"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>

            <div className="login-field">
              <label htmlFor="login-password">
                <LockOutlinedIcon fontSize="small" />
                <span>Senha</span>
              </label>
              <input
                id="login-password"
                type="password"
                placeholder="Digite sua senha"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
            </div>

            <button type="submit" className="login-submit" disabled={loading}>
              {loading ? (
                <span>Entrando...</span>
              ) : (
                <>
                  <LoginIcon fontSize="small" />
                  <span>Entrar</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
